// Standalone Model Hub signup wizard constants.
//
// Credit tiers below MUST stay in sync with benchling/model_hub/utils/model_hub_signup.py
// (MODEL_HUB_CREDITS_PER_PREDICTION and the per-tier MODEL_HUB_SIGNUP_PREDICTIONS_* values).
// The backend is the source of truth for what a new account actually receives.

export type Screen = 'email' | 'verify' | 'profile' | 'work' | 'success';
export type AuthMethod = 'email' | 'google';

// Mirrors EmailDomainType in benchling/model_hub/views/verification.py. Returned by the
// send-code / verify-code endpoints; never inferred on the client.
export type EmailType = 'work' | 'edu' | 'personal';

// The three canonical steps shown in the stepper aside (verification is a sub-screen of Email;
// the plan reveal is folded into the Profile step's credits banner).
export const SIGNUP_STEPS = ['Verify your email', 'Set up your profile', 'Tell us about your work'] as const;

// Mirrors minLength in AUTH_PASSWORD_REQUIREMENTS_CONFIG (benchling/configs/lib/all_options.py).
// The server is the real enforcer (benchling/auth/password_validator.py); this only gates the
// Continue button so users aren't sent to the next step with a password the server will reject.
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_HINT = `At least ${PASSWORD_MIN_LENGTH} characters.`;

export const CREDITS_PER_PREDICTION = 25;

interface CreditTier {
  predictionsPerMonth: number;
  creditsPerMonth: number;
}

export const CREDIT_TIERS: Record<EmailType, CreditTier> = {
  personal: {predictionsPerMonth: 10, creditsPerMonth: 10 * CREDITS_PER_PREDICTION},
  work: {predictionsPerMonth: 250, creditsPerMonth: 250 * CREDITS_PER_PREDICTION},
  edu: {predictionsPerMonth: 10, creditsPerMonth: 10 * CREDITS_PER_PREDICTION},
};

export const TERMS_OF_SERVICE_URL = 'https://www.benchling.com/benchling-ai-terms-of-service';
export const PRIVACY_POLICY_URL = 'https://www.benchling.com/privacy-policy';
export const SIGNIN_URL = '/signin';
export const GOOGLE_OAUTH_BEGIN_URL = '/ext/google-oauth/begin?next=%2Fsignup';
export const MODEL_HUB_MARKETING_URL = 'https://benchling.com/model-hub';
// The standalone Model Hub root. Signup logs the user in server-side, so after success we send
// them here (via a full-page load) to enter the authenticated app; the signed-out "Back to home"
// back affordance on the email step returns here too.
export const HOME_URL = '/';

export const SIGNUP_TAGLINE =
  'Start for free. Get access to the most powerful Scientific AI models in minutes.';

// Inline hint under the email field on step 1. The email tier isn't known until the code is sent,
// so this highlights the personal and verified-industry offers. Numbers mirror the backend grant (see
// CREDIT_TIERS / benchling/model_hub/utils/model_hub_signup.py): personal and academic 10, work 250.
export const EMAIL_CREDITS_HINT =
  'Get 10 free predictions per month with a personal email, or 250 per month with a verified life science ' +
  'industry email.';

// "Your work" survey — analytics only; answers are not persisted server-side.
export const DEPARTMENT_OPTIONS = [
  {value: 'protein_antibody_engineering', label: 'Protein / Antibody Engineering'},
  {value: 'structural_biology_protein_design', label: 'Structural Biology / Protein Design'},
  {value: 'computational_chemistry', label: 'Computational Chemistry'},
  {value: 'medicinal_chemistry', label: 'Medicinal Chemistry'},
  {value: 'bioinformatics', label: 'Bioinformatics'},
  {value: 'it_data_engineering', label: 'IT and Data Engineering'},
  {value: 'other_research', label: 'Other Research'},
];

export const WORK_FOCUS_OPTIONS = [
  {value: 'antibody_discovery', label: 'Antibody discovery'},
  {value: 'protein_engineering', label: 'Protein engineering'},
  {value: 'enzyme_design', label: 'Enzyme design'},
  {value: 'peptides_macrocycles', label: 'Peptides & macrocycles'},
  {value: 'small_molecules', label: 'Small molecules'},
  {value: 'nucleic_acids', label: 'mRNA & nucleic acids'},
  {value: 'structural_biology', label: 'Structural biology'},
  {value: 'other', label: 'Other'},
];
